import * as ko from "knockout";
import * as _ from "lodash";
import * as AuthenticationData from "../data/authenticationData";
import { IApplication } from "../interfaces/application";
import { IController } from "../interfaces/controller";
import { IPageManager, IPageModel } from "../interfaces/pageManager";
import * as Constants from "./constants";
import * as ControllerRegistry from "./controllerRegistry";
import * as EventUtil from "./eventUtil";
import * as Global from "./global";
import * as TabScroller from "./tabScroller";
import * as UI from "./ui";

interface IPageManagerViewModel {
    pages: KnockoutObservableArray<IPageModel>;
    select: (page: IPageModel) => void;
    tabClose: (page: IPageModel, event: any) => void;
    closeAllTabs: () => void;
}

// this is the code that manages all of the open pages in the application.
// it handles switching between pages, loading new pages and displaying
// a list of open pages.
export class PageManager implements IPageManager {

    // redirects to url after successful login attempt by the user
    public redirectURL: string = "";

    // -----------------------------------------------------------------------------
    // private properties
    // -----------------------------------------------------------------------------

    // view model for the left side panel that has the list
    // of open pages
    private pagesViewModel: IPageManagerViewModel;

    // view model for the header that appears above each page
    private pageHeaderViewModel: any = {};

    // the tag all pages are inserted into
    private pagesContainer: JQuery;

    private tabScroller: TabScroller.TabScrollerControl;

    private saveRequestRunning: boolean;

    // -----------------------------------------------------------------------------
    // constructor
    // -----------------------------------------------------------------------------
    constructor() {
        const me: PageManager = this;
        this.saveRequestRunning = false;
        // this view model is used to display links to the open pages
        // note that the current page should always be the first item in the
        // pages array.  This way the pages array is always sorted with most recently
        // viewed pages first.

        this.pagesViewModel = {
            pages: ko.observableArray(),
            // select fires when the user has chosen a new page to view
            select(page: IPageModel): void {
                me.displayLoadedRoute(page.ID());
            },
            tabClose(page: IPageModel, event: any): void {
                event.preventDefault();
                me.tryClosePage(page);
            },
            closeAllTabs: async () => {

                for (const page of this.pagesViewModel.pages()) {
                    page.ContentContainer.hide();
                    page.IsActive(false);
                }

                const pagesToRemove = this.pagesViewModel.pages().filter((page) => page.URL !== me.getDefaultPage());
                for (const page of pagesToRemove) {
                    this.pagesViewModel.select(page);
                    await this.tryClosePage(page);
                }
                this.displayController(me.getDefaultPage(), []);
                this.tabScroller.reset();
            },
        };

        ko.applyBindings(this.pagesViewModel, $("div[data-id='master_Tabs']")[0]);
        this.tabScroller = new TabScroller.TabScrollerControl($("div[data-id='master_Tabs']"));

        this.pageHeaderViewModel = {
            // page name
            title: ko.observable(""),
            subTitle: ko.observable(""),

            // buttons
            showClose: ko.observable(false),
            showSave: ko.observable(false),
            showAdd: ko.observable(false),

            // closePage fires when the user has chosen to close the current page
            onClose(data: any, event: any): void {
                // get the current page
                const page: IPageModel = me.getActivePage();
                me.tryClosePage(page);
            },
            onSave(data: any, event: any): void {
                me.saveCurrentPage();
            },

            onSaveAndClose(data: any, event: any): void {
                const activePage: any = me.getActivePage();

                if (activePage.UIController.save != null && !me.saveRequestRunning) {
                    me.saveRequestRunning = true;
                    activePage.UIController.save().done(() => {
                        me.closePage(activePage.ID());
                    }).always(() => {
                        me.saveRequestRunning = false;
                    });
                }
            },

            onAdd(data: any, event: any): void {
                const activePage: any = me.getActivePage();
                if (activePage.UIController.add != null) {
                    activePage.UIController.add();
                }
            },
        };

        this.pageHeaderViewModel.showSaveAndClose = ko.pureComputed(() => {
            return me.pageHeaderViewModel.showClose() && me.pageHeaderViewModel.showSave();
        });

        ko.applyBindings(this.pageHeaderViewModel, $("div[data-id='master_PageHeader']")[0]);

        // bind to application events
        Global.app().events().on(EventUtil.EVENT_ROUTECHANGE, this.onControllerRouteChange, this);
        Global.app().events().on(EventUtil.EVENT_ROUTECLOSE, (data: EventUtil.IRouteClose) => {
            me.closePage(data.appRouterID);
        }, this);

        // respond to resizing
        $(window).resize(() => {
            me.resizePage();
        });

        // bind to browser close event, so we can display a warning
        // if there are unsaved changes.
        $(window).bind("beforeunload", this.onBeforeUnload.bind(this));

        // save commonly used DOM elements for performance
        this.pagesContainer = $("div[data-id='master_PagesContainer']");

        // size elements to window height
        this.resizePage();
    }

    // -----------------------------------------------------------------------------
    // public Methods
    // -----------------------------------------------------------------------------

    // displays a controller.  If the controller is already open, will show it.
    // if controller is not open, will load it and display it.
    public displayController(controller: string, params: string[]): void {

        const uiController: IController = ControllerRegistry.getController(controller);
        // If User is not logged close everything and go to Default page
        // Default  If logged: Dashboard page
        //          If not Logged: Home page
        if ((!uiController.AllowAnonymousAccess || !uiController.AllowAnonymousAccess()) && !Global.app().isLoggedIn) {
            this.redirectURL = window.location.href;
            this.closeAllAndGotoDefaultPage();
            return;
        }

        // show the control if its already loaded
        if (!this.displayLoadedRoute(uiController.appRouterID(params))) {
            // control not loaded, so add it
            this.addNewPage(uiController, controller, params);
        }
    }

    // update the display to the logged in state
    public login(): void {
        // the left panel is hidden until the user logs in.  Show it now.
        if (!Global.app().hideMenu) {
            // Show the tabs and footer, if the menus aren't being hidden
            $("div[data-id='master_Tabs']").removeClass().addClass("container-fluid");
            $("div[data-id='master_PageHeader']").removeClass("hidden");
            $(".master-page-footer").removeClass("hidden");
        }

        $("div[data-id='master_PagesContainer']").removeClass().addClass("master-page-pages-container");

        this.resizePage();
    }

    // logs the user out of the system and returns them to the home page
    public logout(): void {
        UI.showWaitIndicator(this.pagesContainer);
        this.removeAllPages();
        // Note that we are using "always", even if logout fails
        // continue to log the user out of the website.
        AuthenticationData.Logout().always(() => {
            Global.app().logoutUser();
        });
    }

    // closes all open pages and navigates to a new page
    public closeAllAndNavigate(url: string): void {
        this.removeAllPages();
        this.navigate(url, true);
    }

    public closeAllAndGotoDefaultPage(): void {
        this.removeAllPages();
        this.gotoDefaultPage();
    }

    public gotoDefaultPage(): void {
        if (Global.app().isLoggedIn) {
            this.navigate(this.getDefaultPage(), true);
        } else {
            this.navigate("/Home", true);
        }
    }

    public closeCurrentPage(): void {

        const pages: any = this.pagesViewModel.pages();
        let page: IPageModel;
        let pageIndex: number;

        if (pages.length > 0) {
            pageIndex = this.getActivePageIndex();
            if (pageIndex >= 0) {

                // remove item from the array, which will
                // cause the select page element to disappear
                page = this.pagesViewModel.pages.splice(pageIndex, 1)[0];

                // remove the page contents from the DOM
                $(page.ContentContainer).remove();

                // show the previous tab
                if (pages.length > 0) {
                    if (pageIndex > 0) {
                        this.displayLoadedRoute(pages[pageIndex - 1].ID());
                    } else {
                        this.displayLoadedRoute(pages[0].ID());
                    }
                } else {
                    this.gotoDefaultPage();
                }

                this.tabScroller.resize();
            }
        }
    }

    // used to close the current page and navigate to a different page
    public closeAndNavigate(url: string): void {
        const pages: any = this.pagesViewModel.pages();
        let page: IPageModel;
        let pageIndex: number;

        if (pages.length > 0) {
            pageIndex = this.getActivePageIndex();
            if (pageIndex >= 0) {
                // remove page from the array
                page = this.pagesViewModel.pages.splice(pageIndex, 1)[0];

                // remove the page contents from the DOM
                $(page.ContentContainer).remove();
            }
        }

        // goto the new page
        this.navigate(url, true);
    }

    // used to update the page header and page list when the user entered new data
    public refreshCurrentPageTitle(): void {
        let tabModel: IPageModel;
        let title: string;
        let subTitle: string;

        if (this.pagesViewModel.pages().length <= 0) { return; }

        const pageIndex: number = this.getActivePageIndex();
        if (pageIndex < 0) { return; }

        tabModel = this.pagesViewModel.pages()[pageIndex];
        title = tabModel.UIController.title();
        subTitle = tabModel.UIController.subTitle();

        // update what is displayed in the page description
        tabModel.Title(title);
        tabModel.SubTitle(subTitle);

        // update header
        this.pageHeaderViewModel.title(title);
        this.pageHeaderViewModel.subTitle(subTitle);
    }

    public workspaceHeight(): number {
        // the value subtracted for the height should be adjusted
        // so that no vertical scroll bar appears in the browser
        let height: number = _.defaultTo<number>($(window).height(), 0) - 104;
        if (height < 0) {
            height = 0;
        }
        return height;
    }

    public gotoTaskPage(taskId: number, closeAfter: boolean = false): void {

        const app: IApplication = Global.app();
        let url: string = "";

        if (app.userSession != null && app.userSession.Permissions.indexOf(Constants.PERMISSION_USE_TASK_ENTRY_PAGE) >= 0 ) {
            url = "/TaskEntry/" + taskId;
        } else {
            url = "/Task/" + taskId;
        }

        if (closeAfter) {
            this.closeAndNavigate(url);
        } else {
            app.router().navigate(url, true);

        }
    }

    public saveCurrentPage() {
        const activePage = this.getActivePage();

        if (activePage.UIController.save != null && !this.saveRequestRunning) {
            this.saveRequestRunning = true;
            activePage.UIController.save().always(() => {
                this.saveRequestRunning = false;
            });
        }
    }

    // -----------------------------------------------------------------------------
    // private Methods
    // -----------------------------------------------------------------------------

    // Use to set the default page based on user permissions
    private getDefaultPage(): string {
        const app = Global.app();
        if (app.userSession && app.userSession.Permissions.indexOf(Constants.PERMISSION_TASK_DASHBOARD) >= 0) {
            return Constants.DEFAULT_CONTROLLER_TASK;
        } else {
            return Constants.DEFAULT_CONTROLLER;
        }
    }

    // changes IsActive to false for all pages
    private makeAllPagesInactive(): void {
        _.forEach(this.pagesViewModel.pages(), (page: IPageModel) => {
            if (page.IsActive()) {
                page.IsActive(false);
            }
        });
    }

    // if the page with the specified unique ID is already loaded
    // then display it and return true.
    // otherwise return false.
    private displayLoadedRoute(appRouterID: string): boolean {
        let isLoaded: boolean = false;
        const pages: any = this.pagesViewModel.pages();
        const max: number = pages.length;
        let i: number;
        let page: IPageModel;

        for (i = 0; i < max; i++) {
            page = pages[i];
            if (page.ID() === appRouterID) {
                // tab already loaded, so show it
                isLoaded = true;
                this.hideAllPages();
                page.ContentContainer.show();

                // update the URL, but do not trigger the router
                this.navigate(page.URL, false);

                // make this page the active page
                this.makeAllPagesInactive();
                page.IsActive(true);

                // update header
                this.updatePageHeaderViewModel(page);

                // refresh the page
                if (page.UIController.refresh != null) {
                    page.UIController.refresh();
                }

                break;
            }
        }

        return isLoaded;
    }

    // creates a new page and loads the specified controller control into it.
    private addNewPage(uiController: IController, controller: string, params: any[]): void {
        const me: PageManager = this;
        let container: JQuery;

        // add a div container to the page that will hold the controller
        container = $("<div class='pageContainer'></div>").appendTo(this.pagesContainer);

        // render the controller to the page.  By convention, the template file
        // must have the same name as the controller, and the primary template
        // in the file must be names template_CONTROLLERNAME.
        // also, any items to appear in the left panel must be called actions_CONTROLLERNAME
        Global.app().templates().render(controller.toLowerCase(), "template_" + controller.toLowerCase(), container)
            .done(() => {
                // let the controller do any post rendering
                uiController
                    .render(container)
                    .done((): any => {
                        // render was successful, so show this page and
                        // add it to the drawer menu
                        const appRouterID: any = uiController.appRouterID(params);
                        const pagesViewModel: IPageModel = {
                            ID: ko.observable<string>(appRouterID),
                            Title: ko.observable<string>(uiController.title()),
                            SubTitle: ko.observable<string>(uiController.subTitle()),
                            ContentContainer: container,
                            UIController: uiController,
                            URL: "",
                            IsActive: ko.observable<boolean>(true),
                            // Note that if we are hiding the menu, also hide all Close buttons
                            CanClose: ko.observable<boolean>(controller !== me.getDefaultPage()),
                        };

                        if (uiController.height) {
                            uiController.height(me.workspaceHeight());
                        }

                        pagesViewModel.URL = controller;
                        _.forEach(params, (param: any) => {
                            if (param != null) {
                                pagesViewModel.URL += "/" + param;
                            }
                        });

                        // hide any existing pages
                        me.hideAllPages();

                        // add the new page
                        me.makeAllPagesInactive();
                        me.pagesViewModel.pages.push(pagesViewModel);
                        me.tabScroller.resize();

                        // load content into the controller
                        uiController
                            .load(params)
                            .done(() => {
                                // update the page name to reflect the record that was loaded
                                pagesViewModel.Title(uiController.title());
                                pagesViewModel.SubTitle(uiController.subTitle());

                                // update the header
                                me.updatePageHeaderViewModel(pagesViewModel);

                                // scroll tabs so new tab is visible
                                me.tabScroller.scrollRight();
                            })
                            .fail(() => {
                                // page could not load
                                me.closePage(appRouterID);
                            });
                    });
            });
    }

    private getPageModel(appRouterID: string): IPageModel | null {
        const pages: any = this.pagesViewModel.pages();
        const max: number = pages.length;
        let i: number;
        let page: IPageModel;

        for (i = 0; i < max; i++) {
            page = pages[i];
            if (page.ID() === appRouterID) {
                return page;
            }
        }

        return null;
    }

    private removeAllPages(): void {
        const pages: any = this.pagesViewModel.pages();

        _.forEach(pages, (page: IPageModel) => {
            // remove the page contents from the DOM
            $(page.ContentContainer).remove();
        });
        this.pagesViewModel.pages.removeAll();

        // update header
        this.pageHeaderViewModel.title("");
        this.pageHeaderViewModel.subTitle("");

        this.tabScroller.resize();
    }

    // used to response to EVENT_ROUTECHANGE events thrown by controllers
    // when their state changes which requires a URL change.
    // for example, if a user is clicked in a list, update the URL to reflect
    // the ID of the item that was selected.
    private onControllerRouteChange(data: EventUtil.IRouteChange): void {
        const tabModel: IPageModel | null = this.getPageModel(data.appRouterID);

        if (tabModel != null) {
            // update what is displayed in the page description
            tabModel.Title(data.title);
            tabModel.SubTitle(data.subTitle);
            // save the new URL
            tabModel.URL = data.url;
            // update the URL displayed in the browser, but don't trigger
            // any code to run in response to the change, because the
            // display has already been updated.
            this.navigate(data.url, false);

            // update header
            this.pageHeaderViewModel.title(data.title);
            this.pageHeaderViewModel.subTitle(data.subTitle);

            if (data.newAppRouterID != null &&
                data.newAppRouterID !== "" &&
                data.newAppRouterID !== data.appRouterID) {
                // update the route ID
                tabModel.ID(data.newAppRouterID);
            }
        }
    }

    // used to close an open page.
    private closePage(appRouterID: string): void {

        const pages: any = this.pagesViewModel.pages();
        let max: number;
        let i: number;
        let page: IPageModel;

        max = pages.length;
        for (i = 0; i < max; i++) {
            if (pages[i].ID() === appRouterID) {
                // remove item from the array, which will
                // cause the select page element to disappear
                page = this.pagesViewModel.pages.splice(i, 1)[0];

                // remove the page contents from the DOM
                $(page.ContentContainer).remove();

                // show the previously selected page
                if (pages.length > 0) {
                    if (page.IsActive()) {
                        // user is closing active page, so decide which page to display now
                        if (i > 0) {
                            this.displayLoadedRoute(pages[i - 1].ID());
                        } else {
                            this.displayLoadedRoute(pages[0].ID());
                        }
                    }
                    // else, page being closed is not the active page
                    // so just keep the active page the current page
                } else {
                    this.gotoDefaultPage();
                }

                this.tabScroller.resize();
                break;
            }
        }
    }

    private getActivePage(): IPageModel {
        const page: IPageModel | undefined = _.find(this.pagesViewModel.pages(), (thePage: IPageModel): boolean => {
            return thePage.IsActive();
        });

        if (page) {
            return page;
        } else {
            throw new Error("Unable to find active page");
        }
    }

    private getActivePageIndex(): number {
        return _.findIndex(this.pagesViewModel.pages(), (page: IPageModel): boolean => {
            return page.IsActive();
        });
    }

    // update the header, used when a new page is displayed
    private updatePageHeaderViewModel(page: IPageModel): void {
        this.pageHeaderViewModel.title(page.Title());
        this.pageHeaderViewModel.subTitle(page.SubTitle());
        this.pageHeaderViewModel.showClose(page.CanClose());
        this.pageHeaderViewModel.showSave(
            page.UIController.save != null &&
            (!page.UIController.showSaveButton || page.UIController.showSaveButton()));
        this.pageHeaderViewModel.showAdd(page.UIController.add != null);
    }

    // called when user tries to close the browser or navigate to another page.
    private onBeforeUnload(): string | void {
        // check if any open pages have unsaved changes
        const pages: any = this.pagesViewModel.pages();
        const tabsWithUnsavedChanged: string[] = [];

        _.forEach(pages, (page: IPageModel): any => {
            if (page.UIController.haveFieldsChanged()) {
                tabsWithUnsavedChanged.push(page.Title() + " " + page.SubTitle());
            }
        });

        if (tabsWithUnsavedChanged.length > 0) {
            // verify user wants to leave this page
            // this message will appear in a browser rendered dialog box.  It is not possible
            // to display our own dialog box when the user is leaving the page.

            // note that this message text only shows in IE.  Chrome and Firefox ignore this and show a built-in message.

            const unloadMessage: string =
                "The following pages have unsaved changes:\r\r    " +
                tabsWithUnsavedChanged.join("\r    ") +
                "\r\rAre you sure you want to leave this page and lose any unsaved changes?";

            return unloadMessage;
        }
        return;
    }

    // helper function to call router.navigate to open a new URL
    private navigate(url: string, trigger: boolean): void {
        Global.app().router().navigate(url, trigger);
    }

    private hideAllPages(): void {
        $.each(this.pagesViewModel.pages(), (index: number, page: IPageModel) => {
            page.ContentContainer.hide();
        });
    }

    private resizePage(): void {
        const height: number = this.workspaceHeight();
        const app = Global.app();

        $.each(this.pagesViewModel.pages(), (index: number, page: IPageModel) => {
            if (page.UIController.height) {
                page.UIController.height(height);
            }
        });

        let heightAdjustment = 0;
        if (app.isLoggedIn) {
            if (app.hideMenu) {
                // With menus hidden, we can add a little more height
                heightAdjustment = -35;
            } else {
                // Adjust for the header, menu bar, tabs and footer
                heightAdjustment = 64;
            }
        }

        $("div[data-id='master_PagesContainer']").height(height - heightAdjustment);
        this.tabScroller.resize();
    }

    // close the current page.  If the page has unsaved changes, confirm
    // with the user that they want to close.
    private async tryClosePage(page: IPageModel): Promise<void> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        if (page != null) {
            if (page.UIController.haveFieldsChanged()) {
                UI.showConfirmDialog(
                    Constants.PHRASE_UNSAVEDCHANGESONCLOSE,
                    () => {
                        this.closePage(page.ID());
                        deferred.resolve();
                    },
                    () => {
                        deferred.resolve();
                    });
            } else {
                this.closePage(page.ID());
                deferred.resolve();
            }
        }
        return deferred.promise();
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/common/pageManager.ts